import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import OrderTrackDetail from "./views/OrderTrackDetail/OrderTrackDetail";
import Cart from "./views/Cart/Cart";
import Dashboard from "./views/Dashboard/Dashboard";
import OrderTrackList from "./views/OrderTrackList/OrderTrackList";
import PickupAddress from "./views/PickupAddress/PickupAddress";
import SearchLocation from "./views/SearchLocation/SearchLocation";
import SetDeliveryAddress from "./views/SetDeliveryAddress/SetDeliveryAddress";
import SetPickupAddress from "./views/SetPickupAddress/SetPickupAddress";
import Profile from "./views/Profile/Profile";
import Login from "./views/Login/Login";
import { Provider, shallowEqual, useDispatch, useSelector } from "react-redux";
import store, { persistor } from "./store";
import WalletList from "./views/Wallet/WalletList/WalletList";
import utils from "./resources/utils";
import constant from "./resources/constant";
import Loader from "./modules/Loader";
import TransactionInfo from "./views/Wallet/TransactionInfo/TransactionInfo";
import AddFunds from "./views/Wallet/AddFunds/AddFunds";
import SearchRider from "./views/SearchRider/SearchRider";
import SearchFrequentCustomer from "./views/SearchFrequentCustomer/SearchFrequentCustomer";
import { ThemeProvider, useTheme } from "./ThemeContext";
import { Toaster } from "react-hot-toast";
import SavedAddresses from "./views/SavedAddresses/SavedAddresses";
import SetNewAddress from "./views/SetNewAddress/SetNewAddress";
import BusinessSelect from "./views/BusinessSelect/BusinessSelect";
import _ from "lodash";
import { get_user_details } from "./actions/app";
import PendingOrders from "./views/PendingOrders/PendingOrders";
import CreateTicket from "./views/CreateTicket/CreateTicket";
import { NetworkStatusProvider, useNetworkStatus } from "./NetworkContext";
import NoInternet from "./views/NoInternet/NoInternet";
import { PersistGate } from "redux-persist/integration/react";
import SignUp from "./views/SignUp/SignUp";
import ForgotPassword from "./views/ForgotPassword/ForgotPassword";
import Account from "./views/Account/Account";
import Support from "./views/Support/Support";
import TicketHistory from "./views/Support/TicketHistory";
import ViewTicket from "./views/Support/ViewTicket";
import CreateSupportTicket from "./views/Support/CreateSupportTicket";
import { NotificationsExample } from "./modules/Notification";
import BusinessGuard from "./resources/businessGuard";
import AddressDetails from "./views/SavedAddresses/AddressDetails";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Geolocation } from "@capacitor/geolocation";
import { PushNotifications } from "@capacitor/push-notifications";
import { Contacts } from "@capacitor-community/contacts";
import LanguageSelect from "./views/LanguageSelect/LanguageSelect";
// Higher-order component to protect routes

const PrivateRoute = ({ element, ...rest }) => {
  const dispatch = useDispatch();
  const { updateTheme } = useTheme();
  const { is_loading } = useSelector((state) => state.app, shallowEqual);
  const [is_api_load, set_is_api_load] = useState(false);
  const auth_token = utils._retrieveData(constant.AUTH_TOKEN);
  const type_of_owner = utils._retrieveData(constant.TYPE_OF_OWNER);
  const owner_id = utils._retrieveData(constant.OWNER_ID);
  const parent_id = utils._retrieveData(constant.PARENT_ID);
  if (auth_token && type_of_owner) {
    utils.setRequestAuthHeader(auth_token, type_of_owner);
    utils.setRequestAuthHeaderBusinessId(owner_id, parent_id);
  }

  useEffect(() => {
    const biz_id = utils._retrieveData(constant.SELECTED_BUSINESS_ID);
    if (auth_token && biz_id) {
      dispatch(get_user_details(updateTheme));
    } else {
      set_is_api_load(true);
    }
  }, []);

  return auth_token ? (
    <>
      {is_loading && <Loader />}
      {/* {is_api_load && element} */}
      {element}
    </>
  ) : (
    <Navigate to="/login" />
  );
};

const AppRoutes = () => {
  const navigate = useNavigate();
  const [countryInitialized, setCountryInitialized] = useState(false);

  useEffect(() => {
    const initializeCountry = async () => {
      try {
        const storedCountry = utils._retrieveData(
          constant.SELECTED_COUNTRY
        );

        if (storedCountry) {
          setCountryInitialized(true);
          return;
        }
        const detectedCountry = await utils.detectCountry();

        utils._storeData(
          constant.SELECTED_COUNTRY,
          detectedCountry
        );
      } catch (err) {
        console.error("Country detection failed", err);
      } finally {
        setCountryInitialized(true);
      }
    };

    initializeCountry();
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return undefined;
    }

    const backHandler = CapacitorApp.addListener("backButton", (event) => {
      if (event?.canGoBack) {
        navigate(-1);
      } else {
        CapacitorApp.exitApp();
      }
    });

    return () => {
      backHandler.remove();
    };
  }, [navigate]);

  if (!countryInitialized) {
    return <Loader />;
  }

  return (
    <NetworkStatusProvider>
      <div className="min-h-[100vh]">
        <Routes>
          <Route element={<BusinessGuard />}>
            <Route
              path="/"
              exact={true}
              element={<PrivateRoute element={<PickupAddress />} />}
            />
            <Route
              path="/dashboard"
              element={<PrivateRoute element={<Dashboard />} />}
            />
            <Route path="/cart" element={<PrivateRoute element={<Cart />} />} />
            <Route
              path="/order-track-list"
              element={<PrivateRoute element={<OrderTrackList />} />}
            />
            <Route
              path="/search-location"
              element={<PrivateRoute element={<SearchLocation />} />}
            />
            <Route
              path="/set-delivery-address"
              element={<PrivateRoute element={<SetDeliveryAddress />} />}
            />
            <Route
              path="/set-pickup-address"
              element={<PrivateRoute element={<SetPickupAddress />} />}
            />
            <Route
              path="/order-detail/:order_id"
              element={<PrivateRoute element={<OrderTrackDetail />} />}
            />
            <Route
              path="/wallet-list"
              element={<PrivateRoute element={<WalletList />} />}
            />
            <Route
              path="/transaction-info"
              element={<PrivateRoute element={<TransactionInfo />} />}
            />
            <Route
              path="/add-funds"
              element={<PrivateRoute element={<AddFunds />} />}
            />
            <Route
              path="/search-rider/:order_id"
              element={<PrivateRoute element={<SearchRider />} />}
            />
            <Route
              path="/frequent-customer"
              element={<PrivateRoute element={<SearchFrequentCustomer />} />}
            />
            <Route
              path="/saved-address"
              element={<PrivateRoute element={<SavedAddresses />} />}
            />
            <Route
              path="/set-new-address"
              element={<PrivateRoute element={<SetNewAddress />} />}
            />
            <Route
              path="/ticket-history"
              element={<PrivateRoute element={<TicketHistory />} />}
            />
            <Route
              path="/create-ticket"
              element={<PrivateRoute element={<CreateSupportTicket />} />}
            />
            <Route
              path="/view-ticket/:ticketId"
              element={<PrivateRoute element={<ViewTicket />} />}
            />
            <Route path="/address/:addressId" element={<AddressDetails />} />
            <Route
              path="/pending-orders"
              element={<PrivateRoute element={<PendingOrders />} />}
            />
          </Route>
          {/* <Route path="/business-select" element={<BusinessSelect />} /> */}
          <Route path="/login" element={<Login />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/account"
            element={<PrivateRoute element={<Account />} />}
          />
          <Route
            path="/profile"
            element={<PrivateRoute element={<Profile />} />}
          />
          <Route
            path="/support"
            element={<PrivateRoute element={<Support />} />}
          />
          <Route
            path="/business-select"
            element={<PrivateRoute element={<BusinessSelect />} />}
          />
          <Route
            path="/language-select"
            element={<PrivateRoute element={<LanguageSelect />} />}
          />
          <Route path="/no-internet" exact={true} element={<NoInternet />} />
        </Routes>
      </div>
    </NetworkStatusProvider>
  );
};

function App() {
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      document.body.classList.add("native-safe-area");
    }
    const requestNativePermissions = async () => {
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      try {
        await Geolocation.requestPermissions();
      } catch (error) {
        // Permission may be denied or not supported
      }

      try {
        await Contacts.requestPermissions();
      } catch (error) {
        // Permission may be denied or not supported
      }

      if (process.env.REACT_APP_ENABLE_PUSH === "true") {
        try {
          const permission = await PushNotifications.requestPermissions();
          if (permission?.receive === "granted") {
            await PushNotifications.register();
          }
        } catch (error) {
          // Permission may be denied or not supported
        }
      }
    };

    requestNativePermissions();
  }, []);

  return (
    <ThemeProvider>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <Toaster position="bottom-center" reverseOrder={false} />
          {/* <NotificationsExample /> */}
          <Router>
            <AppRoutes />
          </Router>
        </PersistGate>
      </Provider>
    </ThemeProvider>
  );
}

export default App;
